/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback } from 'react';
import { NativeModules, Platform } from 'react-native';
import { useQueryClient } from '@tanstack/react-query';
import { useAppConfig, useSuperAppContext } from '@airasia-rn/ui';
import { SearchFormState, type SnapRecentSearchesStore } from '../types';
import {
  flattenSnapRecentSearchesForHistory,
  useSnapRecentSearches,
} from './useSnapRecentSearches';
import { snapKeys } from './snap/keys';
import { useSnapSearchViewFeatureFlag } from './useSnapSearchViewFeatureFlag';
import { useSnapNavigation } from '../context/SnapNavigationContext';
import { useOptionalSnapConfig } from '../configs/SnapConfigProvider';
import {
  DEFAULT_CURRENCY,
  DEFAULT_GEO_ID,
  DEFAULT_LANGUAGE,
  SNAP_HOTELS_LIST_PATH,
} from '../configs/constants';
import {
  buildSnapListingUrl,
  buildSnapNativeListingProps,
  type SnapNativeListingProps,
} from '../utils/listingUrl';

type NativeSnapOpenModulePayload = {
  widget: string;
  deeplink?: string;
  snapListingProps?: SnapNativeListingProps;
};

type NativeSnapListingContext = ReturnType<typeof useSuperAppContext> & {
  openNativeSnapListing?: boolean;
  isLoggedIn?: boolean;
  openModule?: (data: NativeSnapOpenModulePayload) => void;
};

export const useHandleSearch = () => {
  const queryClient = useQueryClient();
  const { addSnapRecentSearch } = useSnapRecentSearches();
  const { hotel_snap_unified_listing_enabled, snap_recent_search_max_count } =
    useSnapSearchViewFeatureFlag();
  const { navigateToResultPage } = useSnapNavigation();
  const snapConfig = useOptionalSnapConfig()?.config;
  const { config: appConfig } = useAppConfig();
  const superAppContext = useSuperAppContext() as NativeSnapListingContext;

  const handleSearch = useCallback(
    async (state: SearchFormState) => {
      // Save recent search before navigation
      await addSnapRecentSearch(
        state,
        snap_recent_search_max_count,
        state.fromIsAllAirports,
        state.toIsAllAirports,
      );

      if (
        Platform.OS !== 'web' &&
        NativeModules?.ReactToNative?.sendSearchHistory
      ) {
        const updatedRecentSearches =
          queryClient.getQueryData<SnapRecentSearchesStore>(
            snapKeys.search.recentSearches.all,
          );
        const searchHistoryEntries = flattenSnapRecentSearchesForHistory(
          updatedRecentSearches,
        );

        if (searchHistoryEntries.length) {
          NativeModules.ReactToNative.sendSearchHistory(
            'snap',
            searchHistoryEntries,
          );
        }
      }

      const countryCode =
        snapConfig?.geoId ??
        superAppContext.geoId ??
        appConfig.geoId ??
        DEFAULT_GEO_ID;
      const currency = appConfig.currency ?? DEFAULT_CURRENCY;
      const language = appConfig.locale ?? DEFAULT_LANGUAGE;

      if (Platform.OS !== 'web' && hotel_snap_unified_listing_enabled) {
        navigateToResultPage(state);
        return;
      }

      if (Platform.OS !== 'web') {
        const listingUrl = buildSnapListingUrl({
          state,
          env: appConfig.env,
          countryCode,
          currency,
          language,
          hotel_snap_unified_listing_enabled,
        });

        if (
          superAppContext.openNativeSnapListing &&
          superAppContext.openModule
        ) {
          superAppContext.openModule({
            widget: 'snap_deeplink',
            deeplink: listingUrl.replace(
              SNAP_HOTELS_LIST_PATH,
              '/snap/hotels/list',
            ),
            snapListingProps: buildSnapNativeListingProps({
              state,
              countryCode,
              currency,
              language,
              isLoggedIn: superAppContext.isLoggedIn ?? false,
            }),
          });
          return;
        }

        navigateToResultPage(state);
        return;
      }

      if (Platform.OS === 'web') {
        const listingUrl = buildSnapListingUrl({
          state,
          env: appConfig.env,
          countryCode,
          currency,
          language,
          hotel_snap_unified_listing_enabled,
        });

        if (typeof window !== 'undefined' && window.location?.assign) {
          window.location.assign(listingUrl);
          return;
        }
      }

      navigateToResultPage(state);
    },
    [
      addSnapRecentSearch,
      appConfig.currency,
      appConfig.env,
      appConfig.geoId,
      appConfig.locale,
      hotel_snap_unified_listing_enabled,
      navigateToResultPage,
      queryClient,
      snapConfig?.geoId,
      snap_recent_search_max_count,
      superAppContext.geoId,
      superAppContext.isLoggedIn,
      superAppContext.openModule,
      superAppContext.openNativeSnapListing,
    ],
  );

  return { handleSearch };
};
